import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
	getCollection,
	getCollectionEntries,
	getFieldEntries,
	toItemFieldId
} from '@/core/dcr/fields/registry';
import type { CollectionDef } from '@/core/dcr/fields/types';
import { serializeItem } from '@/core/dcr/serialize/toDcrInput';

import { useDcrFormContext } from '../_context/DcrFormContext';
import { useFieldLabel } from '../_hooks/useFieldLabel';
import { formatFieldValue } from '../_utils/formatFieldValue';
import type { DcrAttachment } from '../Attachments/attachment.types';
import { AttachmentGrid } from '../Attachments/AttachmentGrid/AttachmentGrid';
import { getCollectionItemTitle } from '../Collections/collectionTitle';

import { reviewStepStyles as sx } from './ReviewStep.styles';

type Values = Record<string, unknown>;

type Row = { key: string; label: string; value: string };

type CollectionBlock = {
	key: string;
	label: string;
	items: { title: string; rows: Row[] }[];
};

type ReviewEntry =
	{ type: 'row'; row: Row } | { type: 'collection'; block: CollectionBlock };

const RowView = ({ label, value }: Omit<Row, 'key'>) => (
	<Box sx={sx.row}>
		<Typography sx={sx.label}>{label}</Typography>
		<Typography component='span' variant='body2' sx={sx.value}>
			{value}
		</Typography>
	</Box>
);

export const ReviewStep = () => {
	const { t } = useTranslation();
	const fieldLabel = useFieldLabel();
	const { entityType, picklists, isAvailable } = useDcrFormContext();
	const { control } = useFormContext();
	const values = useWatch({ control }) as Values;
	const attachments = (values.attachments ?? []) as DcrAttachment[];

	const blocks = useMemo(() => {
		const itemRows = (
			collectionKey: string,
			collection: CollectionDef,
			item: Values
		): Row[] =>
			getCollectionEntries(collection.id).flatMap(({ key, def }) => {
				if (
					def.visibility !== 'always' ||
					!isAvailable(toItemFieldId(collectionKey, key), def)
				) {
					return [];
				}

				const value =
					def.kind === 'hcoSearch'
						? (item.parentHco as { name?: string } | null)?.name
						: formatFieldValue(
								def,
								item[key],
								picklists,
								t,
								def.isoKey ? item[def.isoKey] : undefined
							);

				return value ? [{ key, label: fieldLabel(def), value }] : [];
			});

		return getFieldEntries(entityType).flatMap(
			({ key, def }): ReviewEntry[] => {
				if (def.visibility === 'off' || !isAvailable(key, def)) return [];

				if (def.kind === 'collection' && def.collection) {
					const collection = getCollection(def.collection);
					const items = ((values[key] as Values[] | undefined) ?? [])
						.filter(item => !!serializeItem(collection.fields, item))
						.map(item => ({
							title: getCollectionItemTitle(collection.id, item, picklists),
							rows: itemRows(key, collection, item)
						}));

					return items.length > 0
						? [
								{
									type: 'collection',
									block: { key, label: fieldLabel(def), items }
								}
							]
						: [];
				}

				const value = formatFieldValue(def, values[key], picklists, t);
				return value
					? [{ type: 'row', row: { key, label: fieldLabel(def), value } }]
					: [];
			}
		);
	}, [entityType, fieldLabel, isAvailable, picklists, t, values]);

	if (blocks.length === 0 && attachments.length === 0) {
		return (
			<Typography sx={sx.noChangesText}>{t('No changes detected.')}</Typography>
		);
	}

	return (
		<Box sx={sx.root}>
			<Box sx={sx.rows}>
				{blocks.map(entry =>
					entry.type === 'row' ? (
						<RowView
							key={entry.row.key}
							label={entry.row.label}
							value={entry.row.value}
						/>
					) : (
						<CollectionView
							key={entry.block.key}
							label={entry.block.label}
							items={entry.block.items}
						/>
					)
				)}
			</Box>

			{attachments.length > 0 && (
				<Box sx={sx.attachmentsSection}>
					<Typography sx={sx.sectionTitle}>{t('Attachments')}</Typography>
					<AttachmentGrid attachments={attachments} />
				</Box>
			)}
		</Box>
	);
};

const CollectionView = ({ label, items }: Omit<CollectionBlock, 'key'>) => (
	<Box sx={sx.collection}>
		<Typography sx={sx.label}>{label}</Typography>
		{items.map((item, index) => (
			<Box key={index} sx={sx.item}>
				{item.title && <Typography sx={sx.itemTitle}>{item.title}</Typography>}
				{item.rows.map(row => (
					<RowView key={row.key} label={row.label} value={row.value} />
				))}
			</Box>
		))}
	</Box>
);
